import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { isNotUndefined } from "../../../utils/string";
import { IconButton, Box, Button, Tabs, Tab, DialogContent, Dialog, FormControl, InputLabel, Select, SelectChangeEvent, Typography, FormControlLabel, Checkbox } from "@mui/material";
import ClearIcon from '@mui/icons-material/Clear';
import PieChartIcon from '@mui/icons-material/PieChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import CompareIcon from '@mui/icons-material/Compare';
import TableChartIcon from '@mui/icons-material/TableChart';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import { iBasket } from "./types";
import ChartPieProduct from "../../graphics/ChartPie.Products";
import ChartPieTerpenesConcentrations from "../../graphics/ChartPie.TerpenesConcentrations";
import ChartPiePKIndices from "../../graphics/ChartPie.PKIndices";
import ChartBarProducts from "../../graphics/ChartBar.Products";
import ChartBarTerpenes from "../../graphics/ChartBar.Terpenes";
import ChartBarComparison from "../../graphics/ChartBar.Comparison";
import ProductSelector from "../../graphics/selectors/ProductSelector";
import ClassSelector from "../../graphics/selectors/ClassSelector";
import DiseaseCategorySelector from "../../graphics/selectors/DiseaseCategorySelector";
import UnitsSelector from "../../graphics/selectors/UnitsSelector";
import DenominatorSelector from "../../graphics/selectors/DenominatorSelector";
import CompoundSelector from "../../graphics/selectors/CompoundSelector";
import AnalysisTypeSelector from "../../graphics/selectors/AnalysisTypeSelector";
import ChartTableProducts from "../../graphics/ChartTable.Products";
import TotalCompoundsDisplay from "../../graphics/TotalCompoundsDisplay";
import CannabinoidsView from "../../graphics/CannabinoidsView";
import { DataRow, DenominatorType, Item, PKDataRow, TerpeneDataRow, UnitType } from "../../graphics/types";

interface DynamicModalProps<T> {
    basket: iBasket;
    isOpen: boolean;
    handleCloseModal: () => void;
}

const BasketGraphics = <T extends Record<string, any>>({ basket, isOpen, handleCloseModal }: DynamicModalProps<T>) => {
    const [basketValues, setBasketValues] = useState<iBasket>(basket);
    const [productViewType, setProductViewType] = useState<'pie' | 'bar' | 'comparison' | 'table' | 'cannabinoids'>('bar');
    const [terpeneChartType, setTerpeneChartType] = useState<'pie' | 'bar'>('bar');
    const [tabValue, setTabValue] = useState(0);
    const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
    const [compoundFilter, setCompoundFilter] = useState<'all' | 'inCommon' | 'notInCommon'>('all');
    const [showNonNumeric, setShowNonNumeric] = useState<boolean>(true);
    const [selectedClass, setSelectedClass] = useState<string>('All');
    const [selectedUnit, setSelectedUnit] = useState<UnitType>('mg');
    const [selectedDenominator, setSelectedDenominator] = useState<DenominatorType>('item');
    const [selectedDiseaseCategory, setSelectedDiseaseCategory] = useState<string>('All');
    const [diseaseInchiKeys, setDiseaseInchiKeys] = useState<string[]>([]);
    const [selectedCompounds, setSelectedCompounds] = useState<string[]>(['All']);
    const [selectedAnalysisType, setSelectedAnalysisType] = useState<string>('All');

    useEffect(() => {
        setBasketValues(basket);
    }, [basket]);

    // Debug selectedCompounds updates
    useEffect(() => {
        console.log('BasketGraphics - Selected Compounds Updated:', selectedCompounds);
    }, [selectedCompounds]);

    // Debug productViewType updates
    useEffect(() => {
        console.log('BasketGraphics - Product View Type:', productViewType);
    }, [productViewType]);

    // Define tab configurations with filters
    const tabConfigs = useMemo(() => [
        {
            name: 'products',
            label: 'Products',
            filter: (item: Item<DataRow>) => item.HEADER === "List of Products Analysis Values"
        },
        {
            name: 'terpenes',
            label: 'Terpenes Concentrations',
            filter: (item: Item<TerpeneDataRow>) => item.HEADER?.startsWith("Concentration of terpenes")
        },
        {
            name: 'pkIndices',
            label: 'PK Indices',
            filter: (item: Item<PKDataRow>) => item.HEADER?.startsWith("PK Indices")
        }
    ], []);

    // Compute visible tabs based on basket data
    const visibleTabs = useMemo(() => {
        return tabConfigs.filter(tab =>
            basketValues.items.some(item => tab.filter(item))
        );
    }, [basketValues.items, tabConfigs]);

    // Adjust tabValue if current tab is no longer visible
    useEffect(() => {
        if (visibleTabs.length > 0 && tabValue >= visibleTabs.length) {
            setTabValue(0);
        }
    }, [visibleTabs, tabValue]);

    // Filter items for Products tab
    const filteredItems = useMemo(() =>
            basketValues.items.filter((item: Item<any>) => item.HEADER === "List of Products Analysis Values"),
        [basketValues.items]
    );

    // Filter handlers
    const handleProductChange = useCallback((products: string[]) => {
        setSelectedProducts(products);
    }, []);

    const handleCompoundFilterChange = useCallback((event: SelectChangeEvent<'all' | 'inCommon' | 'notInCommon'>) => {
        setCompoundFilter(event.target.value as 'all' | 'inCommon' | 'notInCommon');
    }, []);

    const handleNonNumericChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setShowNonNumeric(event.target.checked);
    }, []);

    const handleClassChange = useCallback((value: string) => {
        setSelectedClass(value);
    }, []);

    const handleUnitChange = useCallback((value: UnitType) => {
        setSelectedUnit(value);
    }, []);

    const handleDenominatorChange = useCallback((value: DenominatorType) => {
        setSelectedDenominator(value);
    }, []);

    const handleCompoundChange = useCallback((compounds: string[]) => {
        console.log('BasketGraphics - Setting Selected Compounds:', compounds);
        setSelectedCompounds(compounds);
    }, []);

    const handleAnalysisTypeChange = useCallback((value: string) => {
        setSelectedAnalysisType(value);
    }, []);

    const handleProductViewChange = (type: 'pie' | 'bar' | 'comparison' | 'table' | 'cannabinoids') => {
        setProductViewType(type);
    };

    const handleTerpeneChartTypeChange = (type: 'pie' | 'bar') => {
        setTerpeneChartType(type);
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    if (!isOpen || visibleTabs.length === 0) {
        return null;
    }

    return (
        <Dialog
            open={isOpen}
            onClose={handleCloseModal}
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    width: '80vw',
                    height: '90vh',
                    maxWidth: 'none',
                    maxHeight: 'none',
                }
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', p: 1 }}>
                <Tabs value={tabValue} onChange={handleTabChange} aria-label="basket graphics tabs">
                    {visibleTabs.map((tab, index) => (
                        <Tab key={tab.name} label={tab.label} />
                    ))}
                </Tabs>
                <IconButton onClick={handleCloseModal} color="primary" sx={{ ml: 'auto' }}>
                    <ClearIcon />
                </IconButton>
            </Box>
            <DialogContent sx={{ padding: '10px' }}>
                {isNotUndefined(basketValues) && (
                    <Box sx={{ mt: 2 }}>
                        {visibleTabs.map((tab, index) => (
                            <React.Fragment key={tab.name}>
                                {tabValue === index && (
                                    <>
                                        {tab.name === 'products' && (
                                            <Box>
                                                <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                                                    <ProductSelector
                                                        items={filteredItems}
                                                        selectedProducts={selectedProducts}
                                                        setSelectedProducts={handleProductChange}
                                                        compoundFilter={compoundFilter}
                                                        setCompoundFilter={setCompoundFilter}
                                                    />
                                                    <UnitsSelector
                                                        selectedUnit={selectedUnit}
                                                        setSelectedUnit={handleUnitChange}
                                                    />
                                                    <Typography sx={{ mx: 1 }}>/</Typography>
                                                    <DenominatorSelector
                                                        selectedDenominator={selectedDenominator}
                                                        setSelectedDenominator={handleDenominatorChange}
                                                    />
                                                    <ClassSelector
                                                        items={filteredItems}
                                                        selectedClass={selectedClass}
                                                        setSelectedClass={handleClassChange}
                                                    />
                                                    <AnalysisTypeSelector
                                                        items={filteredItems}
                                                        selectedAnalysisType={selectedAnalysisType}
                                                        setSelectedAnalysisType={handleAnalysisTypeChange}
                                                    />
                                                    <DiseaseCategorySelector
                                                        selectedDiseaseCategory={selectedDiseaseCategory}
                                                        setSelectedDiseaseCategory={setSelectedDiseaseCategory}
                                                        setDiseaseInchiKeys={setDiseaseInchiKeys}
                                                    />
                                                    <FormControlLabel
                                                        control={<Checkbox checked={showNonNumeric} onChange={handleNonNumericChange} />}
                                                        label="Show Non-Numeric"
                                                        sx={{ m: 0 }}
                                                    />
                                                    <CompoundSelector
                                                        items={filteredItems}
                                                        selectedCompounds={selectedCompounds}
                                                        setSelectedCompounds={handleCompoundChange}
                                                        selectedProducts={selectedProducts}
                                                        selectedClass={selectedClass}
                                                        diseaseInchiKeys={diseaseInchiKeys}
                                                    />
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                                                    <Button
                                                        variant={productViewType === 'pie' ? 'contained' : 'outlined'}
                                                        color={productViewType === 'pie' ? 'primary' : 'inherit'}
                                                        onClick={() => handleProductViewChange('pie')}
                                                        startIcon={<PieChartIcon />}
                                                        size="small"
                                                    >
                                                        Pie Chart
                                                    </Button>
                                                    <Button
                                                        variant={productViewType === 'bar' ? 'contained' : 'outlined'}
                                                        color={productViewType === 'bar' ? 'primary' : 'inherit'}
                                                        onClick={() => handleProductViewChange('bar')}
                                                        startIcon={<BarChartIcon />}
                                                        size="small"
                                                    >
                                                        Bar Chart
                                                    </Button>
                                                    <Button
                                                        variant={productViewType === 'comparison' ? 'contained' : 'outlined'}
                                                        color={productViewType === 'comparison' ? 'primary' : 'inherit'}
                                                        onClick={() => handleProductViewChange('comparison')}
                                                        startIcon={<CompareIcon />}
                                                        size="small"
                                                    >
                                                        Comparison Chart
                                                    </Button>
                                                    <Button
                                                        variant={productViewType === 'table' ? 'contained' : 'outlined'}
                                                        color={productViewType === 'table' ? 'primary' : 'inherit'}
                                                        onClick={() => handleProductViewChange('table')}
                                                        startIcon={<TableChartIcon />}
                                                        size="small"
                                                    >
                                                        Table Data View
                                                    </Button>
                                                    <Button
                                                        variant={productViewType === 'cannabinoids' ? 'contained' : 'outlined'}
                                                        color={productViewType === 'cannabinoids' ? 'primary' : 'inherit'}
                                                        onClick={() => handleProductViewChange('cannabinoids')}
                                                        startIcon={<AnalyticsIcon />}
                                                        size="small"
                                                    >
                                                        View Cannabinoids
                                                    </Button>
                                                    <TotalCompoundsDisplay
                                                        items={filteredItems}
                                                        selectedUnit={selectedUnit}
                                                        selectedDenominator={selectedDenominator}
                                                    />
                                                </Box>
                                                {productViewType === 'pie' ? (
                                                    <ChartPieProduct
                                                        data={basketValues}
                                                        selectedProducts={selectedProducts}
                                                        selectedClass={selectedClass}
                                                        compoundFilter={compoundFilter}
                                                        showNonNumeric={showNonNumeric}
                                                        selectedUnit={selectedUnit}
                                                        selectedDenominator={selectedDenominator}
                                                        diseaseInchiKeys={diseaseInchiKeys}
                                                        selectedCompounds={selectedCompounds}
                                                    />
                                                ) : productViewType === 'bar' ? (
                                                    <ChartBarProducts
                                                        data={filteredItems}
                                                        selectedProducts={selectedProducts}
                                                        setSelectedProducts={handleProductChange}
                                                        compoundFilter={compoundFilter}
                                                        setCompoundFilter={setCompoundFilter}
                                                        showNonNumeric={showNonNumeric}
                                                        setShowNonNumeric={setShowNonNumeric}
                                                        selectedClass={selectedClass}
                                                        setSelectedClass={handleClassChange}
                                                        selectedUnit={selectedUnit}
                                                        setSelectedUnit={handleUnitChange}
                                                        selectedDenominator={selectedDenominator}
                                                        setSelectedDenominator={handleDenominatorChange}
                                                        selectedDiseaseCategory={selectedDiseaseCategory}
                                                        setSelectedDiseaseCategory={setSelectedDiseaseCategory}
                                                        diseaseInchiKeys={diseaseInchiKeys}
                                                        setDiseaseInchiKeys={setDiseaseInchiKeys}
                                                        selectedCompounds={selectedCompounds}
                                                        setSelectedCompounds={handleCompoundChange}
                                                        selectedAnalysisType={selectedAnalysisType}
                                                        setSelectedAnalysisType={handleAnalysisTypeChange}
                                                    />
                                                ) : productViewType === 'comparison' ? (
                                                    <ChartBarComparison
                                                        data={filteredItems}
                                                        selectedProducts={selectedProducts}
                                                        setSelectedProducts={handleProductChange}
                                                        compoundFilter={compoundFilter}
                                                        setCompoundFilter={setCompoundFilter}
                                                        showNonNumeric={showNonNumeric}
                                                        setShowNonNumeric={setShowNonNumeric}
                                                        selectedClass={selectedClass}
                                                        setSelectedClass={handleClassChange}
                                                        selectedUnit={selectedUnit}
                                                        setSelectedUnit={handleUnitChange}
                                                        selectedDenominator={selectedDenominator}
                                                        setSelectedDenominator={handleDenominatorChange}
                                                        selectedDiseaseCategory={selectedDiseaseCategory}
                                                        setSelectedDiseaseCategory={setSelectedDiseaseCategory}
                                                        diseaseInchiKeys={diseaseInchiKeys}
                                                        setDiseaseInchiKeys={setDiseaseInchiKeys}
                                                        selectedCompounds={selectedCompounds}
                                                        setSelectedCompounds={handleCompoundChange}
                                                    />
                                                ) : productViewType === 'table' ? (
                                                    <ChartTableProducts
                                                        data={filteredItems}
                                                        selectedProducts={selectedProducts}
                                                        setSelectedProducts={handleProductChange}
                                                        compoundFilter={compoundFilter}
                                                        setCompoundFilter={setCompoundFilter}
                                                        showNonNumeric={showNonNumeric}
                                                        setShowNonNumeric={setShowNonNumeric}
                                                        selectedClass={selectedClass}
                                                        setSelectedClass={handleClassChange}
                                                        selectedUnit={selectedUnit}
                                                        setSelectedUnit={handleUnitChange}
                                                        selectedDenominator={selectedDenominator}
                                                        setSelectedDenominator={handleDenominatorChange}
                                                        selectedDiseaseCategory={selectedDiseaseCategory}
                                                        setSelectedDiseaseCategory={setSelectedDiseaseCategory}
                                                        diseaseInchiKeys={diseaseInchiKeys}
                                                        setDiseaseInchiKeys={setDiseaseInchiKeys}
                                                        selectedCompounds={selectedCompounds}
                                                        setSelectedCompounds={handleCompoundChange}
                                                    />
                                                ) : (
                                                    <CannabinoidsView
                                                        items={filteredItems}
                                                        selectedUnit={selectedUnit}
                                                        selectedDenominator={selectedDenominator}
                                                        selectedProducts={selectedProducts} />
                                                )}
                                            </Box>
                                        )}
                                        {tab.name === 'terpenes' && (
                                            <Box>
                                                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                                    <Button
                                                        variant={terpeneChartType === 'pie' ? 'contained' : 'outlined'}
                                                        color={terpeneChartType === 'pie' ? 'primary' : 'inherit'}
                                                        onClick={() => handleTerpeneChartTypeChange('pie')}
                                                        startIcon={<PieChartIcon />}
                                                        size="small"
                                                    >
                                                        Pie Chart
                                                    </Button>
                                                    <Button
                                                        variant={terpeneChartType === 'bar' ? 'contained' : 'outlined'}
                                                        color={terpeneChartType === 'bar' ? 'primary' : 'inherit'}
                                                        onClick={() => handleTerpeneChartTypeChange('bar')}
                                                        startIcon={<BarChartIcon />}
                                                        size="small"
                                                    >
                                                        Bar Chart
                                                    </Button>
                                                </Box>
                                                {terpeneChartType === 'pie' ? (
                                                    <ChartPieTerpenesConcentrations data={basketValues} />
                                                ) : (
                                                    <ChartBarTerpenes data={basketValues.items} />
                                                )}
                                            </Box>
                                        )}
                                        {tab.name === 'pkIndices' && (
                                            <ChartPiePKIndices data={basketValues} />
                                        )}
                                    </>
                                )}
                            </React.Fragment>
                        ))}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default BasketGraphics;
